// File: templateService.ts
import {
  CreateTemplateInput,
  GetAllTemplatesParams,
  Template,
  UpdateTemplateInput
} from '@/types/templateTypes';
import {
  GetTokenWithPopupOptions,
  PopupConfigOptions
} from '@auth0/auth0-react';

// Define a more precise type for the getAccessTokenSilently function
type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;

// Update the type for getAccessTokenWithPopup
type GetAccessTokenWithPopupType = (
  options?: GetTokenWithPopupOptions,
  config?: PopupConfigOptions
) => Promise<string | undefined>;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const TEMPLATES_ENDPOINT = `${API_BASE_URL}/templates`;

/**
 * Helper function to acquire Auth0 access token.
 * Tries silently, then falls back to popup.
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
      // scope: "read:templates write:templates" // Add specific scopes if needed for templates
    }
  };
  try {
    let result = await getAccessTokenSilently(authParams);
    // Token acquired
    return result;
  } catch (error: any) {
    console.warn('Silent token acquisition failed for TemplateService:', error);
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      console.log(
        'Falling back to interactive token acquisition for TemplateService...'
      );
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error(
          'Interactive token acquisition failed to return a token for TemplateService.'
        );
      }
      return token;
    }
    throw error; // Rethrow other errors
  }
};

/**
 * Creates a new template.
 * @param input - The data for the new template.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The created template.
 */
export const createTemplate = async (
  input: CreateTemplateInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Template> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(`${TEMPLATES_ENDPOINT}/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error creating template:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to create template. Status: ${response.status}`
    );
  }
  return (await response.json()) as Template;
};

/**
 * Retrieves all templates for the user's company, with optional filters.
 * @param params - Parameters including userId, and optional filters like templateCategoryId and includeArchived.
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns A list of templates.
 */
export const getAllTemplates = async (
  params: GetAllTemplatesParams,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Template[]> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const queryParams = new URLSearchParams();
  if (params.templateCategoryId !== undefined) {
    queryParams.append(
      'templateCategoryId',
      params.templateCategoryId.toString()
    );
  }
  if (params.includeArchived !== undefined) {
    queryParams.append('includeArchived', params.includeArchived.toString());
  }

  const queryString = queryParams.toString();
  const url = queryString
    ? `${TEMPLATES_ENDPOINT}?${queryString}`
    : TEMPLATES_ENDPOINT;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': params.userId
    }
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error fetching all templates:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to fetch templates. Status: ${response.status}`
    );
  }
  return (await response.json()) as Template[];
};

/**
 * Retrieves a specific template by its ID.
 * @param templateId - The ID of the template to retrieve.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The requested template or null if not found.
 */
export const getTemplateById = async (
  templateId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Template | null> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(`${TEMPLATES_ENDPOINT}/${templateId}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    }
  });

  if (response.status === 404) {
    console.warn(`Template with ID '${templateId}' not found.`);
    return null;
  }
  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(`Error fetching template with ID ${templateId}:`, errorData);
    throw new Error(
      errorData.message ||
        `Failed to fetch template. Status: ${response.status}`
    );
  }
  return (await response.json()) as Template;
};

/**
 * Updates an existing template.
 * @param templateId - The ID of the template to update.
 * @param input - The data to update the template with. It must include the template's ID.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The updated template, or null if not found.
 */
export const updateTemplate = async (
  templateId: number,
  input: UpdateTemplateInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Template | null> => {
  // Return null if not found, as per C# service
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  if (input.id !== templateId) {
    console.warn(
      `Mismatch between templateId in path (${templateId}) and input DTO (${input.id}). Ensuring path ID is used for consistency in payload.`
    );
  }
  // Ensure the payload 'id' matches the 'templateId' from the route.
  const payload: UpdateTemplateInput = { ...input, id: templateId };

  const response = await fetch(`${TEMPLATES_ENDPOINT}/${templateId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify(payload)
  });

  if (response.status === 404) {
    console.warn(`Template with ID '${templateId}' not found for update.`);
    return null;
  }
  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(`Error updating template with ID ${templateId}:`, errorData);
    throw new Error(
      errorData.message ||
        `Failed to update template. Status: ${response.status}`
    );
  }
  return (await response.json()) as Template;
};

/**
 * Deletes a template by its ID.
 * @param templateId - The ID of the template to delete.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns True if deletion was successful (204 No Content), false if not found (404).
 */
export const deleteTemplate = async (
  templateId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<boolean> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(`${TEMPLATES_ENDPOINT}/${templateId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    }
  });

  if (response.status === 204) {
    return true; // Successfully deleted
  }
  if (response.status === 404) {
    console.warn(`Template with ID '${templateId}' not found for deletion.`);
    return false; // Not found
  }
  if (!response.ok) {
    let errorDetail = response.statusText;
    try {
      const errorData = await response.json();
      errorDetail = errorData.message || errorDetail;
    } catch (e) {
      // Ignore if parsing fails, use statusText
    }
    console.error(
      `Error deleting template with ID ${templateId}:`,
      errorDetail
    );
    throw new Error(
      `Failed to delete template. Status: ${response.status}, Detail: ${errorDetail}`
    );
  }
  // This part should ideally not be reached if !response.ok throws or 204/404 are handled.
  // Adding for completeness, though implies an unexpected status code not covered above.
  console.warn(
    `Unexpected status code ${response.status} when deleting template with ID ${templateId}.`
  );
  return false;
};

/**
 * Duplicates an existing template with all its questions and settings.
 * @param templateId - The ID of the template to duplicate.
 * @param newName - Optional custom name for the duplicated template.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The newly created template.
 */
export const duplicateTemplate = async (
  templateId: number,
  newName: string | undefined,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Template> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  // For now, we'll simulate the API call by fetching the original template
  // and creating a new one with modified data
  // TODO: Replace with actual backend endpoint when available

  // First, get the original template
  const originalTemplate = await getTemplateById(
    templateId,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  if (!originalTemplate) {
    throw new Error(`Template with ID ${templateId} not found.`);
  }

  // Generate a smart name for the duplicate
  const duplicateName = newName || generateDuplicateName(originalTemplate.name);

  // Create the new template (this will work with existing backend)
  const createInput: CreateTemplateInput = {
    name: duplicateName,
    description: originalTemplate.description,
    templateCategoryId: originalTemplate.templateCategoryId
  };

  const newTemplate = await createTemplate(
    createInput,
    userId,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  // TODO: Once backend supports full duplication, replace the above with:
  // const response = await fetch(`${TEMPLATES_ENDPOINT}/${templateId}/duplicate`, {
  //   method: 'POST',
  //   headers: {
  //     'Content-Type': 'application/json',
  //     Authorization: `Bearer ${accessToken}`,
  //     'X-User-Id': userId
  //   },
  //   body: JSON.stringify({ newName: duplicateName })
  // });
  //
  // if (!response.ok) {
  //   const errorData = await response.json().catch(() => ({ message: response.statusText }));
  //   throw new Error(errorData.message || `Failed to duplicate template. Status: ${response.status}`);
  // }
  //
  // return (await response.json()) as Template;

  console.log(
    `Template "${originalTemplate.name}" duplicated as "${duplicateName}"`
  );
  console.log(
    'Note: Questions and summaries will be copied once backend support is added.'
  );

  return newTemplate;
};

/**
 * Generates a smart name for a duplicated template.
 * Examples: "Template" -> "Template (Copy)", "Template (Copy)" -> "Template (Copy 2)"
 */
const generateDuplicateName = (originalName: string): string => {
  // Check if the name already ends with (Copy) or (Copy N)
  const copyRegex = /^(.+?)(?: \(Copy(?: (\d+))?\))?$/;
  const match = originalName.match(copyRegex);

  if (match) {
    const baseName = match[1];
    const copyNumber = match[3] ? parseInt(match[3], 10) + 1 : 2;

    if (copyNumber === 2 && !match[2]) {
      // First copy: "Template" -> "Template (Copy)"
      return `${baseName} (Copy)`;
    } else {
      // Subsequent copies: "Template (Copy)" -> "Template (Copy 2)"
      return `${baseName} (Copy ${copyNumber})`;
    }
  }

  // Fallback (shouldn't happen with proper regex)
  return `${originalName} (Copy)`;
};
